// The buoy-source registry: the single list every consumer goes through. The map
// plots loadAllStations(); a picked station's obs route via fetchObsFor(); panels
// resolve display metadata via buoySource(). Add a network by adding one entry.

import type { BuoyObs, BuoySource, BuoyStation } from "./types.js";
import { ndbcSource } from "./ndbc.js";
import { aodnSource } from "./aodn.js";

export const BUOY_SOURCES: BuoySource[] = [ndbcSource, aodnSource];

export function buoySource(key: string): BuoySource | undefined {
  return BUOY_SOURCES.find((s) => s.key === key);
}

// Catalogs change slowly; cache the merged load briefly so the map layer and the
// primer's nearest-buoy share ONE fetch instead of each hitting every network.
const CATALOG_TTL_MS = 5 * 60_000;
let inflight: Promise<BuoyStation[]> | null = null;
let cachedAt = 0;

/** Load and merge every source's catalog. A source that fails contributes nothing. */
export function loadAllStations(): Promise<BuoyStation[]> {
  if (inflight !== null && Date.now() - cachedAt < CATALOG_TTL_MS) return inflight;
  cachedAt = Date.now();
  inflight = (async () => {
    const results = await Promise.allSettled(BUOY_SOURCES.map((s) => s.loadCatalog()));
    const out: BuoyStation[] = [];
    for (const r of results) {
      if (r.status === "fulfilled") out.push(...r.value);
    }
    if (out.length === 0) inflight = null; // don't cache a total failure — allow retry
    return out;
  })();
  return inflight;
}

/** Fetch a picked station's observations through its source; null if unknown source. */
export function fetchObsFor(source: string, id: string): Promise<BuoyObs | null> {
  const s = buoySource(source);
  return s ? s.fetchObs(id) : Promise.resolve(null);
}
